import React, { useEffect, useState, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';

// Custom cluster icon
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let diameter = 36;
  if (count >= 100) { size = 'large'; diameter = 50; }
  else if (count >= 10) { size = 'medium'; diameter = 42; }

  return L.divIcon({
    html: `<div style="
      background: ${size === 'large' ? '#dc2626' : size === 'medium' ? '#f59e0b' : '#16a34a'};
      color: white;
      width: ${diameter}px;
      height: ${diameter}px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: ${size === 'large' ? '14px' : '12px'};
      border: 3px solid ${size === 'large' ? '#fca5a5' : size === 'medium' ? '#fde68a' : '#86efac'};
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    ">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(diameter, diameter, true),
  });
};

// Fix default marker icon issue with webpack/react
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom farm marker icon
const farmIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Selected farm marker (larger, different color)
const selectedFarmIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to fit map bounds to markers
const FitBounds = ({ positions }) => {
  const map = useMap();
  
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
    }
  }, [positions, map]);
  
  return null;
};

const FarmMapOverview = ({ farms, selectedFarmId, onFarmSelect, onViewDetails, showViewDetails = true, mapHeight = '400px', compact = false }) => {
  const [farmLocations, setFarmLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const navigate = useNavigate();

  // Fetch farm coordinates from address table
  useEffect(() => {
    const fetchFarmLocations = async () => {
      if (!farms || farms.length === 0) {
        setFarmLocations([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const farmIds = farms.map(f => f.farm_id || f.id);
        
        // Batch fetch in chunks of 200 to avoid query limits
        const chunkSize = 200;
        const allLocations = [];
        
        for (let i = 0; i < farmIds.length; i += chunkSize) {
          const chunk = farmIds.slice(i, i + chunkSize);
          const { data, error } = await supabase
            .from('farm')
            .select(`
              id,
              farmName,
              farmsize,
              organisation,
              address!inner (
                id,
                latitude,
                longitude,
                state,
                city,
                addressFull
              )
            `)
            .in('id', chunk);

          if (!error && data) {
            allLocations.push(...data);
          }
        }

        // Filter to farms with valid coordinates
        const validLocations = allLocations
          .filter(f => {
            const lat = parseFloat(f.address?.latitude);
            const lng = parseFloat(f.address?.longitude);
            return lat && lng && lat !== 0 && lng !== 0 && 
                   lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
          })
          .map(f => ({
            id: f.id,
            name: f.farmName || 'Unnamed Farm',
            size: f.farmsize,
            latitude: parseFloat(f.address.latitude),
            longitude: parseFloat(f.address.longitude),
            state: f.address.state || '',
            city: f.address.city || '',
            address: f.address.addressFull || '',
            organisation: f.organisation
          }));

        setFarmLocations(validLocations);
      } catch (err) {
        console.error('Error fetching farm locations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFarmLocations();
  }, [farms]);

  // Calculate marker positions for bounds fitting
  const positions = useMemo(() => {
    if (selectedFarmId && selectedFarmId !== 'all') {
      const selected = farmLocations.find(f => f.id === parseInt(selectedFarmId) || f.id === selectedFarmId);
      if (selected) return [[selected.latitude, selected.longitude]];
    }
    return farmLocations.map(f => [f.latitude, f.longitude]);
  }, [farmLocations, selectedFarmId]);

  // Default center (Malaysia)
  const defaultCenter = [4.2105, 108.9758];
  const defaultZoom = 6;

  if (loading) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          Farm Map Overview
        </h3>
        <div className="flex items-center justify-center h-80 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-2"></div>
            <p className="text-sm text-gray-500">Loading farm locations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          Farm Map Overview
        </h3>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {farmLocations.length} farm{farmLocations.length !== 1 ? 's' : ''} on map
          </span>
          {farms.length > farmLocations.length && (
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
              {farms.length - farmLocations.length} without coordinates
            </span>
          )}
        </div>
      </div>

      {farmLocations.length === 0 ? (
        <div className="flex items-center justify-center h-80 bg-gray-50 rounded-lg">
          <div className="text-center">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-sm text-gray-500">No farms with location data found</p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: mapHeight }}>
          <MapContainer
            center={defaultCenter}
            zoom={defaultZoom}
            style={{ height: '100%', width: '100%' }}
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {positions.length > 0 && <FitBounds positions={positions} />}
            
            <MarkerClusterGroup
              chunkedLoading
              iconCreateFunction={createClusterCustomIcon}
              maxClusterRadius={50}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              zoomToBoundsOnClick={true}
              disableClusteringAtZoom={16}
            >
              {farmLocations.map((farm) => {
                const isSelected = selectedFarmId && selectedFarmId !== 'all' && 
                  (farm.id === parseInt(selectedFarmId) || farm.id === selectedFarmId);
                
                return (
                  <Marker
                    key={farm.id}
                    position={[farm.latitude, farm.longitude]}
                    icon={isSelected ? selectedFarmIcon : farmIcon}
                  >
                    <Popup>
                      <div className="min-w-[220px] max-w-[280px]">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ fontSize: '14px' }}>🌾</span>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h4 style={{ fontWeight: 600, color: '#111827', fontSize: '13px', margin: 0, lineHeight: '1.3' }}>{farm.name}</h4>
                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>ID: {farm.id}</p>
                          </div>
                        </div>
                        <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '6px', marginBottom: '6px' }}>
                          {farm.size && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '3px' }}>
                              <span style={{ color: '#6b7280' }}>Size</span>
                              <span style={{ fontWeight: 500, color: '#374151' }}>{farm.size} hectares</span>
                            </div>
                          )}
                          {(farm.city || farm.state) && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '3px' }}>
                              <span style={{ color: '#6b7280' }}>Location</span>
                              <span style={{ fontWeight: 500, color: '#374151' }}>{[farm.city, farm.state].filter(Boolean).join(', ')}</span>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                            <span style={{ color: '#6b7280' }}>Coordinates</span>
                            <span style={{ fontWeight: 500, color: '#374151' }}>{farm.latitude.toFixed(4)}, {farm.longitude.toFixed(4)}</span>
                          </div>
                        </div>
                        {showViewDetails && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onViewDetails) {
                                onViewDetails(farm.id);
                              } else {
                                navigate(`/farm-management?farmId=${farm.id}`);
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '6px 12px',
                              backgroundColor: '#16a34a',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              marginTop: '4px'
                            }}
                          >
                            <span>View Details</span>
                            <span style={{ fontSize: '14px' }}>→</span>
                          </button>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
          </MapContainer>
        </div>
      )}
    </div>
  );
};

export default FarmMapOverview;
